/**
 * Ubicar la tarjeta dentro de la foto, y la transformación que la endereza.
 *
 * Es el paso del que cuelga casi todo lo demás:
 *
 *   - Las zonas del catálogo son fracciones de la TARJETA. Sin ubicarla, se
 *     recortan fracciones de la FOTO, y cuando la tarjeta no llena el cuadro el
 *     domicilio y el CUIL se van afuera del recorte.
 *   - El tapado del Nº de Trámite por plantilla necesita saber dónde está la
 *     tarjeta. Sin eso no hay garantía, hay una apuesta.
 *   - El alto del renglón en píxeles —lo que permite juzgar si una persona puede
 *     leer— sale de la escala de la tarjeta.
 *
 * Todo este archivo es puro: entra una matriz de píxeles, salen coordenadas. Se
 * prueba sin navegador.
 */
import { Rectangulo } from "./types";
import { TARJETA_ID1 } from "./tuning";
import { ImagenPlana } from "./quality";

export interface Punto {
  x: number;
  y: number;
}

export interface TarjetaUbicada {
  /** Las cuatro esquinas en coordenadas de la imagen que se pasó, en orden
   *  superior-izquierda, superior-derecha, inferior-derecha, inferior-izquierda. */
  esquinas: [Punto, Punto, Punto, Punto];
  /** Relación de lados medida sobre el cuadrilátero encontrado. */
  relacionDeLados: number;
  /** Fracción de la foto que ocupa la tarjeta. */
  ocupacion: number;
}

export type MotivoSinTarjeta =
  | "imagen_muy_chica"
  | "sin_contraste_con_el_fondo"
  | "forma_no_es_una_tarjeta";

export type ResultadoUbicacion =
  | { ok: true; tarjeta: TarjetaUbicada; detalle: string[] }
  | { ok: false; motivo: MotivoSinTarjeta; detalle: string[] };

/** Umbral de Otsu sobre un histograma de 256 niveles. */
export function umbralOtsu(histograma: number[]): number {
  const total = histograma.reduce((a, b) => a + b, 0);
  if (total === 0) return 128;
  let sumaTotal = 0;
  for (let i = 0; i < 256; i++) sumaTotal += i * histograma[i];

  let sumaFondo = 0;
  let pesoFondo = 0;
  let mejorVarianza = -1;
  let mejorUmbral = 128;

  for (let t = 0; t < 256; t++) {
    pesoFondo += histograma[t];
    if (pesoFondo === 0) continue;
    const pesoFrente = total - pesoFondo;
    if (pesoFrente === 0) break;
    sumaFondo += t * histograma[t];
    const mediaFondo = sumaFondo / pesoFondo;
    const mediaFrente = (sumaTotal - sumaFondo) / pesoFrente;
    const entre = pesoFondo * pesoFrente * (mediaFondo - mediaFrente) ** 2;
    if (entre > mejorVarianza) {
      mejorVarianza = entre;
      mejorUmbral = t;
    }
  }
  return mejorUmbral;
}

function lumaDe(d: ImagenPlana["data"], i: number): number {
  return (d[i] * 299 + d[i + 1] * 587 + d[i + 2] * 114) / 1000;
}

/**
 * Máscara del objeto claro dominante.
 *
 * El documento es más claro que casi cualquier fondo sobre el que se lo apoya
 * —una mesa, un escritorio, una mano—. No es infalible (un papel blanco debajo
 * lo arruina), y por eso el resultado se valida después contra la relación de
 * lados en vez de creerle.
 */
export function mascaraClara(img: ImagenPlana): { mask: Uint8Array; umbral: number } {
  const hist = new Array<number>(256).fill(0);
  const n = img.width * img.height;
  const gris = new Uint8Array(n);
  for (let i = 0; i < n; i++) {
    const v = Math.round(lumaDe(img.data, i * 4));
    gris[i] = v;
    hist[v]++;
  }
  const umbral = umbralOtsu(hist);
  const mask = new Uint8Array(n);
  for (let i = 0; i < n; i++) mask[i] = gris[i] > umbral ? 1 : 0;
  return { mask, umbral };
}

/** Componente conexo más grande de la máscara, por barrido iterativo. */
export function componenteMasGrande(
  mask: Uint8Array,
  w: number,
  h: number
): { puntos: Punto[]; area: number } {
  const visto = new Uint8Array(mask.length);
  let mejor: Punto[] = [];

  const pila = new Int32Array(mask.length);
  for (let inicio = 0; inicio < mask.length; inicio++) {
    if (mask[inicio] === 0 || visto[inicio]) continue;
    let tope = 0;
    pila[tope++] = inicio;
    visto[inicio] = 1;
    const actual: Punto[] = [];

    while (tope > 0) {
      const i = pila[--tope];
      const x = i % w;
      const y = (i - x) / w;
      actual.push({ x, y });
      if (x > 0 && mask[i - 1] && !visto[i - 1]) { visto[i - 1] = 1; pila[tope++] = i - 1; }
      if (x < w - 1 && mask[i + 1] && !visto[i + 1]) { visto[i + 1] = 1; pila[tope++] = i + 1; }
      if (y > 0 && mask[i - w] && !visto[i - w]) { visto[i - w] = 1; pila[tope++] = i - w; }
      if (y < h - 1 && mask[i + w] && !visto[i + w]) { visto[i + w] = 1; pila[tope++] = i + w; }
    }
    if (actual.length > mejor.length) mejor = actual;
  }
  return { puntos: mejor, area: mejor.length };
}

/**
 * Las cuatro esquinas de un conjunto de puntos.
 *
 * Se toman los extremos de x+y y de x−y, que es el truco de siempre para un
 * cuadrilátero convexo: la esquina superior izquierda es la que minimiza x+y, la
 * inferior derecha la que lo maximiza, y las otras dos salen de x−y.
 *
 * Funciona mientras la tarjeta no esté girada cerca de 45°, que no es un caso
 * real: nadie apoya un documento en diagonal. Si pasara, la validación por
 * relación de lados lo rechaza en vez de devolver un cuadrilátero absurdo.
 */
export function esquinasDe(puntos: Punto[]): [Punto, Punto, Punto, Punto] | null {
  if (puntos.length < 4) return null;
  let si = puntos[0], id = puntos[0], sd = puntos[0], ii = puntos[0];
  let minSuma = Infinity, maxSuma = -Infinity, minResta = Infinity, maxResta = -Infinity;
  for (const p of puntos) {
    const suma = p.x + p.y;
    const resta = p.x - p.y;
    if (suma < minSuma) { minSuma = suma; si = p; }
    if (suma > maxSuma) { maxSuma = suma; id = p; }
    if (resta > maxResta) { maxResta = resta; sd = p; }
    if (resta < minResta) { minResta = resta; ii = p; }
  }
  return [si, sd, id, ii];
}

function distancia(a: Punto, b: Punto): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/**
 * Ubica la tarjeta, o dice por qué no pudo.
 *
 * Decir por qué no pudo no es cortesía: de eso depende que el modal pueda
 * avisar que el tapado del trámite no está garantizado, en vez de prometerlo.
 */
export function ubicarTarjeta(img: ImagenPlana): ResultadoUbicacion {
  const detalle: string[] = [];
  if (img.width < 80 || img.height < 50) {
    return { ok: false, motivo: "imagen_muy_chica", detalle };
  }

  const { mask, umbral } = mascaraClara(img);
  detalle.push(`umbral de Otsu: ${umbral}`);

  const { puntos, area } = componenteMasGrande(mask, img.width, img.height);
  const ocupacion = area / (img.width * img.height);
  detalle.push(`objeto claro dominante: ${(ocupacion * 100).toFixed(1)}% de la foto`);

  if (ocupacion < 0.08 || ocupacion > 0.97) {
    // Abajo: o el documento es diminuto en el cuadro, o el fondo es tan claro
    // como él. Arriba: el objeto claro es la foto entera, o sea que no se pudo
    // separar la tarjeta de nada. En los dos casos no sabemos dónde termina el
    // documento, que es lo único que importa.
    return { ok: false, motivo: "sin_contraste_con_el_fondo", detalle };
  }

  const esquinas = esquinasDe(puntos);
  if (!esquinas) return { ok: false, motivo: "forma_no_es_una_tarjeta", detalle };

  // Si las cuatro esquinas son las de la FOTO, no encontramos la tarjeta:
  // encontramos el marco. Pasó en las tres fotos del corpus, y como esas fotos
  // vienen recortadas al documento la relación de lados daba parecida y el
  // control lo dejaba pasar. El daño no es teórico: se estiraba la imagen un 5%
  // para forzarla a ID-1, y se anunciaba una garantía de tapado sobre una
  // plantilla apoyada en esquinas que nunca se midieron.
  const margen = Math.max(2, Math.round(Math.min(img.width, img.height) * 0.01));
  const pegadaAlBorde = (p: Punto) =>
    (p.x <= margen || p.x >= img.width - 1 - margen) &&
    (p.y <= margen || p.y >= img.height - 1 - margen);
  if (esquinas.every(pegadaAlBorde)) {
    detalle.push("las cuatro esquinas son las de la foto: es el marco, no la tarjeta");
    return { ok: false, motivo: "sin_contraste_con_el_fondo", detalle };
  }

  const [si, sd, id, ii] = esquinas;
  const ancho = (distancia(si, sd) + distancia(ii, id)) / 2;
  const alto = (distancia(si, ii) + distancia(sd, id)) / 2;
  if (alto === 0) return { ok: false, motivo: "forma_no_es_una_tarjeta", detalle };

  const relacionDeLados = ancho / alto;
  const esperada = TARJETA_ID1.relacionDeLados;
  const desvio = Math.abs(relacionDeLados - esperada) / esperada;
  detalle.push(
    `relación de lados ${relacionDeLados.toFixed(3)} contra ${esperada.toFixed(3)} esperada (desvío ${(desvio * 100).toFixed(1)}%)`
  );

  if (desvio > TARJETA_ID1.toleranciaRelacion) {
    // Puede ser otro objeto, o la tarjeta cortada por el borde del cuadro. En
    // cualquier caso no se puede afirmar dónde están las zonas.
    return { ok: false, motivo: "forma_no_es_una_tarjeta", detalle };
  }

  return { ok: true, tarjeta: { esquinas, relacionDeLados, ocupacion }, detalle };
}

/** Inclinación del borde superior, en grados. Positiva en sentido horario. */
export function inclinacionGrados(t: TarjetaUbicada): number {
  const [si, sd] = t.esquinas;
  return (Math.atan2(sd.y - si.y, sd.x - si.x) * 180) / Math.PI;
}

/**
 * Homografía que lleva cuatro puntos a otros cuatro.
 *
 * Ocho incógnitas, ocho ecuaciones, eliminación de Gauss. Corrige inclinación y
 * perspectiva de una sola vez, que es la razón por la que enderezar y recortar
 * son la misma operación y no dos.
 *
 * Lo que NO corrige: la distorsión de lente. Una homografía manda rectas a
 * rectas, y el barril de una webcam curva los bordes, así que el ajuste calza en
 * las esquinas y queda con error en el medio.
 */
export function homografia(origen: Punto[], destino: Punto[]): number[] | null {
  if (origen.length !== 4 || destino.length !== 4) return null;
  const A: number[][] = [];
  const b: number[] = [];
  for (let i = 0; i < 4; i++) {
    const { x, y } = origen[i];
    const { x: u, y: v } = destino[i];
    A.push([x, y, 1, 0, 0, 0, -u * x, -u * y]);
    b.push(u);
    A.push([0, 0, 0, x, y, 1, -v * x, -v * y]);
    b.push(v);
  }

  const n = 8;
  for (let col = 0; col < n; col++) {
    let pivote = col;
    for (let f = col + 1; f < n; f++) {
      if (Math.abs(A[f][col]) > Math.abs(A[pivote][col])) pivote = f;
    }
    if (Math.abs(A[pivote][col]) < 1e-10) return null;
    [A[col], A[pivote]] = [A[pivote], A[col]];
    [b[col], b[pivote]] = [b[pivote], b[col]];

    for (let f = 0; f < n; f++) {
      if (f === col) continue;
      const factor = A[f][col] / A[col][col];
      if (factor === 0) continue;
      for (let c = col; c < n; c++) A[f][c] -= factor * A[col][c];
      b[f] -= factor * b[col];
    }
  }

  const h = b.map((v, i) => v / A[i][i]);
  return [...h, 1];
}

export function aplicar(H: number[], p: Punto): Punto {
  const d = H[6] * p.x + H[7] * p.y + H[8];
  return {
    x: (H[0] * p.x + H[1] * p.y + H[2]) / d,
    y: (H[3] * p.x + H[4] * p.y + H[5]) / d,
  };
}

/** Rectángulo destino para una tarjeta ID-1 de un ancho dado. */
export function destinoTarjeta(anchoPx: number): { ancho: number; alto: number; esquinas: Punto[] } {
  const alto = Math.round(anchoPx / TARJETA_ID1.relacionDeLados);
  return {
    ancho: anchoPx,
    alto,
    esquinas: [
      { x: 0, y: 0 },
      { x: anchoPx, y: 0 },
      { x: anchoPx, y: alto },
      { x: 0, y: alto },
    ],
  };
}

/** Caja del catálogo —fracciones de tarjeta— llevada a la foto original. */
export function cajaEnLaFoto(caja: Rectangulo, t: TarjetaUbicada): Punto[] {
  const [si, sd, id, ii] = t.esquinas;
  const H = homografia(
    [{ x: 0, y: 0 }, { x: 1, y: 0 }, { x: 1, y: 1 }, { x: 0, y: 1 }],
    [si, sd, id, ii]
  );
  if (!H) return [];
  return [
    { x: caja.x, y: caja.y },
    { x: caja.x + caja.w, y: caja.y },
    { x: caja.x + caja.w, y: caja.y + caja.h },
    { x: caja.x, y: caja.y + caja.h },
  ].map((p) => aplicar(H, p));
}

export interface FormaDelCuadrilatero {
  /** Cuánto difieren los lados opuestos, como fracción del mayor. */
  desparejoDeLados: number;
  /** Desvío máximo de los cuatro ángulos respecto de 90°, en grados. */
  desvioDeAngulos: number;
  inclinacionGrados: number;
  /**
   * Si la tarjeta ya está derecha y sin perspectiva. Cuando es así NO hay que
   * warpearla: alcanza con recortarla.
   *
   * Esto existe porque warpear una foto que ya estaba bien la empeora dos
   * veces. La escorza, si las esquinas detectadas tienen aunque sea un poco de
   * error —y el método de los extremos de x±y lo tiene—, y la remuestrea, que
   * ablanda el texto chico. Una foto sacada de frente a un documento apoyado no
   * necesita nada de eso.
   */
  yaEstaDerecha: boolean;
}

function anguloEn(a: Punto, vertice: Punto, b: Punto): number {
  const u = { x: a.x - vertice.x, y: a.y - vertice.y };
  const v = { x: b.x - vertice.x, y: b.y - vertice.y };
  const cos =
    (u.x * v.x + u.y * v.y) / (Math.hypot(u.x, u.y) * Math.hypot(v.x, v.y) || 1);
  return (Math.acos(Math.max(-1, Math.min(1, cos))) * 180) / Math.PI;
}

export function formaDelCuadrilatero(
  t: TarjetaUbicada,
  opciones: { gradosDerecho?: number; desparejoMaximo?: number } = {}
): FormaDelCuadrilatero {
  const gradosDerecho = opciones.gradosDerecho ?? 1.5;
  const desparejoMaximo = opciones.desparejoMaximo ?? 0.02;
  const [si, sd, id, ii] = t.esquinas;

  const arriba = Math.hypot(sd.x - si.x, sd.y - si.y);
  const abajo = Math.hypot(id.x - ii.x, id.y - ii.y);
  const izq = Math.hypot(ii.x - si.x, ii.y - si.y);
  const der = Math.hypot(id.x - sd.x, id.y - sd.y);
  const desparejo = Math.max(
    Math.abs(arriba - abajo) / Math.max(arriba, abajo, 1),
    Math.abs(izq - der) / Math.max(izq, der, 1)
  );

  const angulos = [
    anguloEn(ii, si, sd),
    anguloEn(si, sd, id),
    anguloEn(sd, id, ii),
    anguloEn(id, ii, si),
  ];
  const desvioDeAngulos = Math.max(...angulos.map((a) => Math.abs(a - 90)));
  const inclinacion = inclinacionGrados(t);

  return {
    desparejoDeLados: desparejo,
    desvioDeAngulos,
    inclinacionGrados: inclinacion,
    yaEstaDerecha:
      Math.abs(inclinacion) <= gradosDerecho &&
      desvioDeAngulos <= gradosDerecho &&
      desparejo <= desparejoMaximo,
  };
}

/** Caja alineada a los ejes que contiene al cuadrilátero. */
export function cajaContenedora(t: TarjetaUbicada): Rectangulo {
  const xs = t.esquinas.map((p) => p.x);
  const ys = t.esquinas.map((p) => p.y);
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  return { x, y, w: Math.max(...xs) - x, h: Math.max(...ys) - y };
}
